import { baseApi } from './baseApi';
import { Attachment, AttachmentResponse, SingleUploadRequest } from 'models/Attachment';
import { QueryParams, TAG_TYPES } from 'types';
import { getQueryParams } from 'utils';

const URL_ATTACHMENT = 'attachments';

const attachmentApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttachments: builder.query<
      {
        data: {
          rows: Attachment[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_ATTACHMENT, method: 'GET' };
        return {
          url: `${URL_ATTACHMENT}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Attachment, id })),
              { type: TAG_TYPES.Attachment, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Attachment, id: 'LIST' }];
      },
    }),

    getAttachmentById: builder.query<AttachmentResponse, string>({
      query: (id) => ({
        url: `${URL_ATTACHMENT}/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Attachment, id }],
    }),

    createAttachment: builder.mutation<AttachmentResponse, Omit<Attachment, 'id' | 'createdAt' | 'updatedAt'>>({
      query: (data) => ({
        url: URL_ATTACHMENT,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Attachment, id: 'LIST' }],
    }),

    updateAttachment: builder.mutation<AttachmentResponse, Partial<Attachment> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${URL_ATTACHMENT}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Attachment, id },
        { type: TAG_TYPES.Attachment, id: 'LIST' },
      ],
    }),

    deleteAttachment: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_ATTACHMENT}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Attachment, id },
        { type: TAG_TYPES.Attachment, id: 'LIST' },
      ],
    }),

    singleUpload: builder.mutation<AttachmentResponse, SingleUploadRequest>({
      query: (data) => ({
        url: `${URL_ATTACHMENT}/single-upload`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Attachment, id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAttachmentsQuery,
  useGetAttachmentByIdQuery,
  useCreateAttachmentMutation,
  useUpdateAttachmentMutation,
  useDeleteAttachmentMutation,
  useSingleUploadMutation,
} = attachmentApiService;
